import {
  Box,
  Button,
  Checkbox,
  Flex,
  GreyGrey30,
  GreyGrey60,
  GreyGrey90,
  IconArrowUpRight,
  IconExclamationTriangle,
  Primary13Regular,
  Primary13SemiBold,
  Tooltip,
  highlightQueryMatch
} from '@sequencehq/core-components'
import { inflect } from 'inflection'
import { match } from 'ts-pattern'
import { CustomerValidationTooltip } from './CustomerValidationTooltip'
import { formatListWithOverflow } from './formatListWithOverflow'
import { SelectableCustomer } from './integrationCustomer.types'

const CONTACTS_VISIBLE_PER_ROW = 1

export const IntegrationCustomerRow = (props: {
  customer: SelectableCustomer
  search: string
  isSelected: boolean
  isDisabled: boolean
  integrationName?: string | null
  onToggle: () => void
  'data-testid': string
}) => {
  const { customer } = props
  const dataTestId = props['data-testid']

  const label = (
    <Flex color={GreyGrey90} flexDirection="column" gap="2px" minWidth={0}>
      <Box {...Primary13SemiBold}>
        {highlightQueryMatch(customer.name, props.search)}
        {customer.country && (
          <Box as="span" {...Primary13Regular} color={GreyGrey60}>
            {' '}
            ({customer.country})
          </Box>
        )}
      </Box>
      <CustomerRowSecondLine
        customer={customer}
        integrationName={props.integrationName}
        data-testid={dataTestId}
      />
    </Flex>
  )

  return (
    <Flex
      padding="16px 24px"
      width="100%"
      gap="8px"
      alignItems="center"
      justifyContent="space-between"
      _notLast={{ borderBottom: `1px solid ${GreyGrey30}` }}
    >
      <Box flex="1" minWidth={0}>
        <Checkbox
          data-testid={dataTestId}
          label={label}
          isChecked={props.isSelected}
          disabled={props.isDisabled}
          onChange={props.onToggle}
        />
      </Box>
      {customer.externalUrl && (
        <Tooltip label={`View in ${props.integrationName}`}>
          <Button
            as="a"
            href={customer.externalUrl}
            target="_blank"
            rel="noreferrer noopener"
            variant="ghost"
            icon={<IconArrowUpRight />}
            aria-label={`View in ${props.integrationName}`}
            data-testid={`${dataTestId}.externalLink`}
          />
        </Tooltip>
      )}
    </Flex>
  )
}

const CustomerRowSecondLine = (props: {
  customer: SelectableCustomer
  integrationName?: string | null
  'data-testid': string
}) => {
  const { customer } = props
  const hasValidationErrors = Boolean(
    customer.invalidReason || customer.validationErrors?.length
  )

  if (hasValidationErrors) {
    const errorCount = customer.validationErrors?.length ?? 0
    const onlyMissing = (customer.validationErrors ?? []).every(
      error => error.problem === 'MISSING'
    )
    const summary = match({ errorCount, onlyMissing })
      .with({ errorCount: 0 }, () => 'Missing or invalid fields')
      .with(
        { onlyMissing: true },
        () => `Missing required ${inflect('field', errorCount)}`
      )
      .otherwise(() => `Missing or invalid ${inflect('field', errorCount)}`)

    return (
      <Tooltip
        label={
          <CustomerValidationTooltip
            invalidReason={customer.invalidReason}
            validationErrors={customer.validationErrors}
            integrationName={props.integrationName}
            data-testid={`${props['data-testid']}.validation.tooltip`}
          />
        }
      >
        <Flex
          gap="8px"
          alignItems="center"
          {...Primary13Regular}
          color={GreyGrey90}
          width="fit-content"
          data-testid={`${props['data-testid']}.validation`}
        >
          <IconExclamationTriangle />
          {summary}
        </Flex>
      </Tooltip>
    )
  }

  if (!customer.contacts?.length) {
    return null
  }

  return (
    <Box
      {...Primary13Regular}
      color={GreyGrey60}
      data-testid={`${props['data-testid']}.contactEmails`}
    >
      {formatListWithOverflow(
        customer.contacts.map(contact => contact.email),
        CONTACTS_VISIBLE_PER_ROW
      )}
    </Box>
  )
}
